import * as types from '../mutation-types';
import ConversationAPI from '../../api/conversations';

const state = {
  records: {},
  uiFlags: {
    isFetching: false,
    isUpdating: false,
    isError: false,
  },
};

// The API is intentionally per-conversation. Keeping these guards outside the
// Vuex state avoids exposing request bookkeeping to persisted store snapshots.
const operationVersions = new Map();
const pendingOperations = new Map();
const pendingReconciliations = new Map();
const labelsWrittenByVersion = new Map();

const nextOperationVersion = conversationId => {
  const id = Number(conversationId);
  const version = (operationVersions.get(id) || 0) + 1;
  operationVersions.set(id, version);
  return version;
};

const currentOperationVersion = conversationId =>
  operationVersions.get(Number(conversationId)) || 0;

const reconcileLabels = (dispatch, conversationId) => {
  const id = Number(conversationId);
  if (pendingReconciliations.has(id)) return pendingReconciliations.get(id);

  const queue = pendingOperations.get(id);
  const reconciliation = (queue ? queue.tail : Promise.resolve())
    .catch(() => false)
    .then(() => dispatch('get', conversationId))
    .finally(() => pendingReconciliations.delete(id));

  pendingReconciliations.set(id, reconciliation);
  return reconciliation;
};

const updateLabels = async (
  { commit },
  { conversationId, labels, request }
) => {
  const id = Number(conversationId);
  const operationKey = `${request.name}:${labels.join('\u0000')}`;
  const queue = pendingOperations.get(id) || {
    failed: false,
    keys: new Set(),
    tail: Promise.resolve(),
  };
  if (queue.keys.has(operationKey)) return false;

  queue.keys.add(operationKey);
  pendingOperations.set(id, queue);
  // Invalidate reads already in flight as soon as the user changes labels.
  nextOperationVersion(id);
  commit(types.default.SET_CONVERSATION_LABELS_UI_FLAG, {
    isUpdating: true,
  });

  const operation = queue.tail.then(async () => {
    // A GET launched while this request is pending must not win after it.
    const operationVersion = nextOperationVersion(id);
    try {
      const response = await request.call(
        ConversationAPI,
        conversationId,
        labels
      );
      if (operationVersion === currentOperationVersion(id)) {
        commit(types.default.SET_CONVERSATION_LABELS, {
          id: conversationId,
          data: response.data.payload,
        });
        labelsWrittenByVersion.set(id, operationVersion);
      }
      queue.failed = false;
      return true;
    } catch (error) {
      queue.failed = true;
      return false;
    } finally {
      // Invalidate reads started while the request was being completed.
      nextOperationVersion(id);
      queue.keys.delete(operationKey);
      if (!queue.keys.size) {
        pendingOperations.delete(id);
        commit(types.default.SET_CONVERSATION_LABELS_UI_FLAG, {
          isUpdating: false,
          isError: queue.failed,
        });
      }
    }
  });

  queue.tail = operation.catch(() => false);
  return operation;
};

export const getters = {
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getConversationLabels: $state => id => {
    return $state.records[Number(id)] || [];
  },
};

export const actions = {
  get: async ({ commit }, conversationId) => {
    const requestVersion = currentOperationVersion(conversationId);
    commit(types.default.SET_CONVERSATION_LABELS_UI_FLAG, {
      isFetching: true,
    });
    try {
      const response = await ConversationAPI.getLabels(conversationId);
      if (
        !pendingOperations.has(Number(conversationId)) &&
        requestVersion === currentOperationVersion(conversationId)
      ) {
        commit(types.default.SET_CONVERSATION_LABELS, {
          id: conversationId,
          data: response.data.payload,
        });
        labelsWrittenByVersion.delete(Number(conversationId));
      }
      commit(types.default.SET_CONVERSATION_LABELS_UI_FLAG, {
        isFetching: false,
      });
    } catch (error) {
      commit(types.default.SET_CONVERSATION_LABELS_UI_FLAG, {
        isFetching: false,
      });
    }
  },
  update: ({ commit }, { conversationId, labels }) =>
    updateLabels(
      { commit },
      {
        conversationId,
        labels,
        request: ConversationAPI.updateLabels,
      }
    ),
  add: ({ commit }, { conversationId, labels }) =>
    updateLabels(
      { commit },
      {
        conversationId,
        labels,
        request: ConversationAPI.addLabels,
      }
    ),
  remove: ({ commit }, { conversationId, labels }) =>
    updateLabels(
      { commit },
      {
        conversationId,
        labels,
        request: ConversationAPI.removeLabels,
      }
    ),
  setBulkConversationLabels({ commit, dispatch }, conversations) {
    commit(
      types.default.SET_BULK_CONVERSATION_LABELS,
      conversations.filter(
        conversation => !labelsWrittenByVersion.has(Number(conversation.id))
      )
    );
    return Promise.all(
      conversations
        .filter(conversation =>
          labelsWrittenByVersion.has(Number(conversation.id))
        )
        .map(conversation => reconcileLabels(dispatch, conversation.id))
    );
  },
  setConversationLabel({ commit, dispatch }, { id, data }) {
    if (labelsWrittenByVersion.has(Number(id))) {
      return reconcileLabels(dispatch, id);
    }

    return commit(types.default.SET_CONVERSATION_LABELS, { id, data });
  },
};

export const mutations = {
  [types.default.SET_CONVERSATION_LABELS_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.default.SET_CONVERSATION_LABELS]: ($state, { id, data }) => {
    $state.records = { ...$state.records, [id]: data };
  },
  [types.default.SET_BULK_CONVERSATION_LABELS]: ($state, conversations) => {
    const updatedRecords = { ...$state.records };
    conversations.forEach(conversation => {
      updatedRecords[conversation.id] = conversation.labels;
    });

    $state.records = updatedRecords;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
